import React, { useState, useCallback, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  buildLlmPromptButtonLabelSuffix,
  estimateLlmInputTokens,
} from "@utils/llmPromptTokenLabel";

export interface GlobalAdminLlmPromptButtonProps {
  /** Shown in the dialog title */
  dialogTitle: string;
  /** Exact string sent to the LLM for this section; button hidden if missing/blank */
  promptText: string | undefined;
  /** Wall-clock seconds for the generation/section (rules API); shown when finite */
  wallTimeSec?: number | null;
}

/**
 * Shown on compliance accordions when the rules API included `llmPromptsBySection`
 * for the current user (global administrators). Opens a scrollable dialog with copy.
 */
export const GlobalAdminLlmPromptButton: React.FC<
  GlobalAdminLlmPromptButtonProps
> = ({ dialogTitle, promptText, wallTimeSec }) => {
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = (promptText ?? "").trim();
  const charCount = trimmed.length;
  const estTokens = estimateLlmInputTokens(trimmed);
  const labelSuffix = buildLlmPromptButtonLabelSuffix(trimmed, wallTimeSec);
  const wallSuffix =
    wallTimeSec != null &&
    Number.isFinite(wallTimeSec) &&
    wallTimeSec >= 0
      ? `${Number(wallTimeSec.toFixed(1))}s`
      : "";

  const handleCopy = useCallback(async () => {
    const text = (promptText ?? "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("copied");
      return;
    } catch {
      /* fall through to textarea selection */
    }
    const el = textareaRef.current;
    if (!el) {
      setCopyStatus("failed");
      return;
    }
    el.focus();
    el.select();
    el.setSelectionRange(0, text.length);
    try {
      const ok = document.execCommand("copy");
      setCopyStatus(ok ? "copied" : "failed");
    } catch {
      setCopyStatus("failed");
    }
  }, [promptText]);

  if (!trimmed) {
    return null;
  }

  return (
    <>
      <Button
        component="span"
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          setCopyStatus("idle");
          setOpen(true);
        }}
        sx={{
          flexShrink: 0,
          fontSize: "11px",
          py: 0.25,
          px: 1,
          minWidth: "auto",
          borderColor: "#94a3b8",
          color: "#475569",
          textTransform: "none",
        }}
      >
        LLM prompt{labelSuffix}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {dialogTitle}
          {wallSuffix ? (
            <Box
              component="span"
              sx={{ display: "block", fontSize: "12px", fontWeight: 400, mt: 0.5 }}
            >
              Wall clock: {wallSuffix}
            </Box>
          ) : null}
          <Typography
            component="span"
            variant="caption"
            sx={{ display: "block", fontWeight: 400, mt: 0.5, color: "#64748b" }}
          >
            {charCount.toLocaleString()} characters (~
            {estTokens.toLocaleString()} est. tokens) — scroll for facility-type
            and state blocks after the JSON schema
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            inputRef={textareaRef}
            value={trimmed}
            multiline
            fullWidth
            variant="outlined"
            InputProps={{
              readOnly: true,
              sx: {
                fontSize: "12px",
                fontFamily: "ui-monospace, monospace",
                lineHeight: 1.45,
                bgcolor: "#f8fafc",
                "& textarea": {
                  maxHeight: "70vh",
                  overflow: "auto !important",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopy}>
            {copyStatus === "copied"
              ? "Copied"
              : copyStatus === "failed"
                ? "Copy failed"
                : `Copy (${charCount.toLocaleString()} chars)`}
          </Button>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
